// Every user-facing product string for the Pro web client lives here.
//
// Keith directive: the naming ("Commander Bracket Pro", "Deck Chat", "Go Pro")
// and the price strings are PROVISIONAL. Components import from this module and
// never inline a product name, a price, or a piece of error copy, so a rename or
// a price change is a one-file edit instead of a grep-and-pray.
//
// House rule: no em dashes anywhere in this file (hyphens, or rewrite).
//
// Prices here are FALLBACKS only. When RevenueCat is configured its offerings
// are the price source; these strings are what the paywall shows while RC is
// unconfigured (buttons disabled) or while offerings are still loading.
//
// Localization (Phase 3, step 3b.1). The English text lives in the `pro`
// namespace (src/locales/en/pro.json) and every export here is a FUNCTION
// that reads it at call time. Never a module-time t() call: that would
// freeze English before initLanguage() has loaded the chosen language.
// Catalog keys are the snake_case of the iOS ProCopy.swift names where iOS
// has the same string (chatSubtitle -> chat_subtitle), so ProCopy.swift can
// reuse them in Phase 4. Brand names are their own keys (product_name,
// chat_name) and reach every other string as a placeholder.
import i18n, { codeLabel, formatDateBrowser, formatUsd } from '../../i18n'

type Vars = Record<string, string | number>

/** A `pro` namespace lookup, at call time. */
function t(key: string, vars?: Vars): string {
  return i18n.t(key, { ns: 'pro', ...vars })
}

export const productName = (): string => t('product_name')
export const chatName = (): string => t('chat_name')
export const goProLabel = (): string => t('go_pro_label')
export const proBadgeLabel = (): string => t('pro_badge_label')

// Home's Pro band. Names the FEATURE rather than the tier: "Pro" alone tells
// someone who has never opened Deck Chat nothing about what they would be
// buying. The title and aria match iOS `ProCopy.homeBand*`; the subtitle
// diverges on purpose (2026-10-06): web also names the set check and ad
// removal, and iOS has neither a set-check screen nor ads.
export const homeBandTitle = (): string => t('home_band_title', { chat: chatName() })
export const homeBandSubtitle = (): string => t('home_band_subtitle')
export const homeBandAria = (): string => t('home_band_aria', { product: productName() })

// Shown under the plan buttons. States the three things a renewing
// subscription has to state before the money moves: that it renews, that the
// trial converts if untouched, and where to cancel. Then the agreement, with
// the Terms and Privacy Policy links as tags <0> and <1>: ONE sentence for
// the translator (Phase 3, 3g), rendered by ProPurchasePanel with the
// <n> tag convention. Its English text content is byte-identical to the old
// "...agree to our" + JSX links.
export const paywallLegal = (): string => t('paywall_legal')

// The support address in the subscription copy. Code, not catalog text, so a
// translation cannot break the one way out a stuck subscriber has.
const SUPPORT_EMAIL = 'keith@commanderbracket.app'

// The fallback prices. The AMOUNTS are code, never catalog text: a
// translation can reword the period ("/mo") but cannot touch the number a
// customer is quoted. RevenueCat's own formatted price replaces these once
// the offering loads (ProPurchasePanel).
//
// Phase 3, 3c: the amount is a NUMBER, formatted at call time by formatUsd.
// With VITE_I18N off (and in English) that is exactly "$5.99" / "$54.99";
// with it on, every other language gets the same USD amount in its own
// notation ("5,99 US$", "US$ 5,99", ja "$5.99"). The currency never changes.
const PRICE_MONTHLY_USD = 5.99
const PRICE_ANNUAL_USD = 54.99
export const priceMonthlyFallback = (): string => t('price_monthly_fallback', { price: formatUsd(PRICE_MONTHLY_USD) })
export const priceAnnualFallback = (): string => t('price_annual_fallback', { price: formatUsd(PRICE_ANNUAL_USD) })

// The trial hook, shown above the plan buttons when RevenueCat reports a free
// trial on a plan. The day count comes from the product's own trial phase, so
// this line cannot disagree with what checkout actually grants. Wording note:
// the first payment after a trial is the trial CONVERTING, never a "renewal" -
// see the /terms billing-copy history before rewording.
export const paywallTrialHook = (days: number): string => t('paywall_trial_hook', { days })

// FALLBACK for the Pro monthly message allowance quoted in the paywall
// benefits, used only until /pro/status has answered. The authority is the
// server: `pro_cap_month` on /pro/status is the Pro tier's cap read from the
// PRO_MONTHLY_MESSAGES variable, and the paywall renders that whenever it is
// present. This constant shipped as 200 while prod ran 300, which is exactly
// the drift a client-side number invites; it is kept only so the sheet has
// something to say before (or without) a status read. The counter renders
// /pro/status quota caps as before.
export const PRO_MONTHLY_MESSAGES = 300

// ---------------------------------------------------------------------------
// Panel chrome
// ---------------------------------------------------------------------------

export const chatSubtitle = (): string => t('chat_subtitle')
export const chatPlaceholder = (): string => t('chat_placeholder')
// The assistant's avatar letter (decorative, aria-hidden). Added in 3g.
export const chatAvatarLetter = (): string => t('chat_avatar_letter')
// The composer's accessible name ("Ask Deck Chat"). Added in 3g.
export const chatInputAria = (): string => t('chat_input_aria', { chat: chatName() })
// The dock header chip on a restored past conversation (rendered upper-case
// by CSS). Added in 3g.
export const chatDockHistoryChip = (): string => t('chat_dock_history_chip')
// A card chip's preview when Scryfall has no image, and its buy link.
// Added in 3g.
export const chatCardImageMissing = (): string => t('chat_card_image_missing')
export const chatCardBuyOn = (vendor: string): string => t('chat_card_buy_on', { vendor })
export const chatSendLabel = (): string => t('chat_send_label')

// The composer button is a glyph in both states, so these labels are the only
// name a screen reader (or a voice-control user) ever gets for it. Mid-turn the
// send button BECOMES the stop button: one control, one place.
export const chatStopAria = (): string => t('chat_stop_aria')

// The picker in the composer pill, behind VITE_CHAT_ATTACHMENTS.
export const chatAttachAria = (): string => t('chat_attach_aria')
// The x on a staged thumbnail. A glyph again, so this is the only name a screen
// reader or a voice-control user has for it.
export const chatAttachRemoveAria = (): string => t('chat_attach_remove_aria')

// Rendered while a turn is in flight and the server has not sent a tool label
// yet. Server progress labels are rendered VERBATIM, never re-mapped.
export const chatThinkingLabel = (): string => t('chat_thinking_label')

// Swapped in when more than VERIFYING_DELAY_MS passes after the last progress
// frame with no answer yet: that silence is the server's faithfulness check.
export const chatVerifyingLabel = (): string => t('chat_verifying_label')
export const VERIFYING_DELAY_MS = 3000

// Shown once when the deck changes under an open session.
export const chatDeckUpdatedDivider = (): string => t('chat_deck_updated_divider', { chat: chatName() })

// The user pressed stop and no answer had landed. Deliberately flat: the turn
// ended because they said so, and there is nothing to apologize for or explain.
export const chatStoppedRow = (): string => t('chat_stopped_row')
// The stream died mid-turn (backgrounded tab, dropped network) but the server
// finishes the turn and saves the answer; the client polls the transcript.
export const chatRecoveringRow = (): string => t('chat_recovering_row')
export const chatRecoveryFailedRow = (): string => t('chat_recovery_failed_row')

// Header of a conversation opened from the history list. It says the two things
// the user cannot see for themselves: this is old, and typing here does not
// resume it (a new session opens, linked to this one).
export const chatHistoryViewingDivider = (): string => t('chat_history_viewing_divider')

// The same divider when the restored conversation is about a DIFFERENT
// commander than the deck this chat was opened from. Launch night a restored
// Wolverine transcript rendered under an Anti-Venom header and its green ramp
// suggestions read as off-color adds for a mono-white deck - the user filed
// it as a color-identity breach. The transcript must say whose it is.
export const chatHistoryViewingOtherCommander = (commander: string): string =>
  t('chat_history_viewing_other_commander', { commander })

// The conversation list itself (ChatHistory). The title names the contents, not
// the control that opened it.
export const chatHistoryTitle = (): string => t('chat_history_title')
export const chatHistoryEmpty = (): string => t('chat_history_empty')
// Badge on the row for the conversation already on screen, which is shown but
// not openable.
export const chatHistoryCurrent = (): string => t('chat_history_current')
// The panel's own dismiss control. Named for the panel rather than for the
// glyph, which is a bare x. (iOS: chatHistoryCloseLabel.)
export const chatHistoryCloseLabel = (): string => t('chat_history_close_label')

// The round button that appears over the composer once the transcript has been
// scrolled away from the bottom. The glyph is an arrow, so this label is the
// only name a screen reader gets for it.
export const chatJumpLatestAria = (): string => t('chat_jump_latest_aria')

// The empty-state chips. Each one is a message wearing two faces:
//
// - `message` is FROZEN WIRE VOCABULARY (2026-08-25). The API's lane-seeding
//   module (mtg-api app/pro_chat_lane.py) exact-matches these three strings
//   (case-folded) to pick a pre-seeded evidence lane, and iOS
//   ProCopy.chatQuickActions sends the same bytes. Changing one here silently
//   downgrades that entry to the ordinary path (safe, never wrong-lane) -
//   update the lane table in the same change. It is NEVER translated: Deck
//   Chat answers in English this release, and a translated message would
//   miss the lane (Phase 3 plan, "Frozen English strings").
// - `labelKey` is the chip's text, translated (`pro:chat_quick_actions.*`),
//   and is what the user's own bubble shows (sent as displayText).
export interface ChatQuickAction {
  id: string
  message: string
  labelKey: string
}

export const CHAT_QUICK_ACTIONS: readonly ChatQuickAction[] = [
  {
    id: 'upgrade_first',
    // eslint-disable-next-line i18next/no-literal-string -- frozen wire vocabulary (app/pro_chat_lane.py), never translated
    message: 'What should I upgrade first?',
    labelKey: 'chat_quick_actions.upgrade_first',
  },
  {
    id: 'budget_swaps',
    // eslint-disable-next-line i18next/no-literal-string -- frozen wire vocabulary (app/pro_chat_lane.py), never translated
    message: 'Find budget swaps',
    labelKey: 'chat_quick_actions.budget_swaps',
  },
  {
    id: 'win_condition',
    // eslint-disable-next-line i18next/no-literal-string -- frozen wire vocabulary (app/pro_chat_lane.py), never translated
    message: 'What is my win condition?',
    labelKey: 'chat_quick_actions.win_condition',
  },
]

/** A quick-action chip's label in the active language. */
export function chatQuickActionLabel(action: ChatQuickAction): string {
  return t(action.labelKey)
}

/**
 * What a RESTORED user row shows. The server stores the English `message` a
 * quick-action chip sent, so a transcript restored on reload or from the
 * history list maps an exact match back to the chip's label in the active
 * language - the same text the live bubble showed. Display only: the stored
 * and wire text stay English. A user who TYPED the exact English text gets
 * the label too, deliberately (it is the same question).
 */
export function restoredUserText(text: string): string {
  const action = CHAT_QUICK_ACTIONS.find(a => a.message === text)
  return action ? chatQuickActionLabel(action) : text
}

// ---------------------------------------------------------------------------
// Dock chrome (the floating bubble host)
// ---------------------------------------------------------------------------

// The pill label is hidden on very narrow screens, so the aria-label is the
// only name a screen reader ever gets for the collapsed dock.
export const chatDockOpenAria = (): string => t('chat_dock_open_aria', { chat: chatName() })
export const chatDockCloseAria = (): string => t('chat_dock_close_aria', { chat: chatName() })
// The mobile header's back chevron. Plain "Back" on purpose: on a full-screen
// takeover the chevron is the platform's own back affordance, and a screen
// reader user expects it to say what every other back control says.
export const chatBackAria = (): string => t('chat_back_aria')
// The header's history control. Named for what it opens, not for the toggle.
export const chatHistoryAria = (): string => t('chat_history_aria')
export const chatDockDialogAria = (): string => chatName()
// Announced by the activity dots on the collapsed pill while a turn runs.
export const chatDockWorkingAria = (): string => t('chat_dock_working_aria', { chat: chatName() })
// Announced by the unread dot when an answer landed while the dock was closed.
export const chatDockUnreadAria = (): string => t('chat_dock_unread_aria', { chat: chatName() })

// The result page's two chat entries (the collapsed pill and the Improve
// sheet row) carry the free quota as a STATE, never as a separate Pro card.
export function chatEntryFreeLabel(remaining: number): string {
  return t('chat_entry_free_label', { chat: chatName(), count: remaining })
}
export const chatEntryExhaustedLabel = (): string =>
  t('chat_entry_exhausted_label', { chat: chatName(), pro: proBadgeLabel() })

// ---------------------------------------------------------------------------
// Signed-out gate
// ---------------------------------------------------------------------------

export const chatSigninPrompt = (): string => t('chat_signin_prompt', { chat: chatName() })
export const chatSigninCta = (): string => t('chat_signin_cta')

// ---------------------------------------------------------------------------
// Assistant message chrome
// ---------------------------------------------------------------------------

// Tooltip on the subtle tick shown for a verified answer.
export const chatVerifiedTitle = (): string => t('chat_verified_title')

// A turn that cost nothing: the chat could not answer and refunded the
// message, or it only re-offered swaps it had already given you. Stated
// plainly under the bubble so the counter and the transcript agree.
export const chatNotCounted = (): string => t('chat_not_counted')

// ---------------------------------------------------------------------------
// Live evidence (the fact lines that fill in while a turn runs)
// ---------------------------------------------------------------------------
//
// Every string below renders a value the SERVER composed deterministically from
// a verified tool payload. The phrasing is ours; the numbers and names are
// never ours. If a value is absent from the payload its clause is dropped
// entirely - a fact line must never carry a number nobody measured.
//
// The extraction that decides which clauses survive lives in
// chatEvidenceFormat.ts; these are only the sentences.

// The collapsed affordance on a finished answer. These lines are the receipts,
// so they are hidden behind a disclosure rather than deleted.
export const chatEvidenceTitle = (): string => t('chat_evidence_title')
export const chatEvidenceAria = (): string => t('chat_evidence_aria', { chat: chatName() })

// The disclosure trigger. The count is there so the affordance is worth opening
// before it is opened. (iOS: chatEvidenceToggle.)
export function chatEvidenceToggleCopy(count: number): string {
  return t('chat_evidence_toggle', { title: chatEvidenceTitle(), n: count })
}
// The live list, announced politely so a screen reader user hears findings land
// instead of sitting through a silent spinner.
export const chatEvidenceLiveAria = (): string => t('chat_evidence_live_aria')

export function chatEvidenceBracket(bracket: number): string {
  return t('chat_evidence_bracket', { bracket })
}

// `turn` arrives pre-formatted (the payload's own precision, not a rounding of
// ours). The tilde is honest about it being an estimate.
export function chatEvidenceWinTurn(turn: string): string {
  return t('chat_evidence_win_turn', { turn })
}

export function chatEvidenceEngine(engine: string): string {
  return t('chat_evidence_engine', { engine })
}

export function chatEvidenceCandidates(names: string[]): string {
  return t('chat_evidence_candidates', { count: names.length, names: names.join(', ') })
}

// The achieved bracket is only named when the payload carried one: an adjust
// that reports no bracket has not proved it reached anything.
export function chatEvidenceAdjust(swapCount: number, achievedBracket: number | null): string {
  return achievedBracket === null
    ? t('chat_evidence_adjust_swaps', { count: swapCount })
    : t('chat_evidence_adjust_reached', { count: swapCount, bracket: achievedBracket })
}

export function chatEvidenceCombos(found: number): string {
  if (found === 0) return t('chat_evidence_combos_none')
  return t('chat_evidence_combos', { count: found })
}

export function chatEvidenceLookup(names: string[]): string {
  return t('chat_evidence_lookup', { names: names.join(', ') })
}

// `found` is the server's post-filter total, which can exceed the names shown
// (the payload caps its card list); saying "Found 102: A, B, C" is honest
// about both. Zero is a finding, not an absence.
export function chatEvidenceSearch(found: number, names: string[]): string {
  if (found === 0) return t('chat_evidence_search_none')
  return names.length > 0
    ? t('chat_evidence_search_named', { count: found, names: names.join(', ') })
    : t('chat_evidence_search', { count: found })
}

// ---------------------------------------------------------------------------
// Tappable follow-up choices (the chips under an assistant bubble)
// ---------------------------------------------------------------------------

export const chatChoicesAria = (): string => t('chat_choices_aria')

// ---------------------------------------------------------------------------
// Free goal clarification (the chips a vague "tune up my deck" gets, before
// anything is charged - server flag PRO_CHAT_GOAL_CLARIFY)
// ---------------------------------------------------------------------------

// The bubble text is CLIENT-authored: the server's clarify frame carries only
// the goal chips, no prose.
export const chatClarifyPrompt = (): string => t('chat_clarify_prompt')
export const chatClarifyAria = (): string => t('chat_clarify_aria')

// ---------------------------------------------------------------------------
// Per-answer feedback (the thumbs row under an assistant bubble)
// ---------------------------------------------------------------------------
//
// ONE SHOT per answer: after a pick both buttons disable and the choice stays
// visible with no undo. A rating that can be toggled repeatedly is noise in the
// feedback store, and a re-send per toggle would be worse.

export const chatFeedbackUpAria = (): string => t('chat_feedback_up_aria')
export const chatFeedbackDownAria = (): string => t('chat_feedback_down_aria')

// Shown next to the (now disabled) buttons once a rating has been sent.
export const chatFeedbackThanks = (): string => t('chat_feedback_thanks')

// ---------------------------------------------------------------------------
// Swap cards (ChatSwaps)
// ---------------------------------------------------------------------------

export const chatSwapsTitle = (): string => t('chat_swaps_title')

// Swaps start ACCEPTED (the chat only ships validated ones), but the state is
// shown as two explicit controls rather than one toggle: a single line reading
// "Accepted - skip" made users ask what it would do, which is exactly the
// question a control on a destructive-ish action must never raise. The pressed
// chip names the CURRENT state, the other names the available action.
export const chatSwapAcceptLabel = (): string => t('chat_swap_accept_label')
export const chatSwapSkipLabel = (): string => t('chat_swap_skip_label')

// Post-apply the row is a record, not a control, so it reads as an outcome.
export const chatSwapStatusApplied = (): string => t('chat_swap_status_applied')
export const chatSwapStatusSkipped = (): string => t('chat_swap_status_skipped')

// Header count above the rows. Only useful with more than one swap; the Apply
// button already says how many are going in.
export function chatSwapSummaryCopy(accepted: number, total: number): string {
  return t('chat_swap_summary', { accepted, total })
}

export const chatSwapNoReplacement = (): string => t('chat_swap_no_replacement')
// The two reason lines under a swap ("Out: <why it goes>", "In: <why it
// comes in>"); the reasons are the server's. Added in 3g.
export const chatSwapOutLabel = (): string => t('chat_swap_out_label')
export const chatSwapInLabel = (): string => t('chat_swap_in_label')
export const chatSwapOwnedBadge = (): string => t('chat_swap_owned_badge')
export const chatSwapGameChangerBadge = (): string => t('chat_swap_game_changer_badge')
export const chatSwapGameChangerTitle = (): string => t('chat_swap_game_changer_title')
export const chatSwapComboBadge = (): string => t('chat_swap_combo_badge')
export const chatSwapComboTitle = (): string => t('chat_swap_combo_title')

// The trigger for the alternates picker. Reads as a count because that is the
// fact the user cares about before opening it.
export function chatSwapAlternatesCopy(count: number): string {
  return t('chat_swap_alternates', { count })
}

// Alternates replace the CUT, never the add (same data model as the adjust
// panel): the engine picked a card to bring in and offered several cards it
// could leave for.
export const chatSwapAlternatesAria = (): string => t('chat_swap_alternates_aria')
export const chatSwapAlternateBadge = (): string => t('chat_swap_alternate_badge')
export const chatSwapAlternateTitle = (): string => t('chat_swap_alternate_title')
// Visible text stays a prefix of chatSwapAlternatePickAria ("Cut Sol Ring
// instead") so the accessible name CONTAINS the visible label: WCAG 2.5.3
// label-in-name, which a voice-control user needs to say "click Cut".
export const chatSwapAlternatePickLabel = (): string => t('chat_swap_alternate_pick_label')
export const chatSwapAlternatePickedLabel = (): string => t('chat_swap_alternate_picked_label')

// An alternate the deck has no spare copy of cannot be cut, so it is offered
// disabled with the reason rather than hidden.
export const chatSwapAlternateUnavailable = (): string => t('chat_swap_alternate_unavailable')

export function chatSwapAlternatePickAria(name: string): string {
  return t('chat_swap_alternate_pick_aria', { card: name })
}

// The picked alternate's (disabled) chip. Same label-in-name rule: the visible
// "Cutting" is a prefix of this name.
export function chatSwapAlternatePickedAria(name: string): string {
  return t('chat_swap_alternate_picked_aria', { card: name })
}

export function chatSwapAlternateUndoCopy(name: string): string {
  return t('chat_swap_alternate_undo', { card: name })
}

export function chatSwapApplyLabel(count: number): string {
  return t('chat_swap_apply_label', { count })
}

// The deck editor's Deck Coach panel (Wave 6b): every swap applies on its own
// ("Apply") or all remaining ones at once ("Apply all"), each one undo step
// in the editor. The accessible name keeps the visible "Apply" as its prefix
// (WCAG 2.5.3 label-in-name) and names the card coming in.
export const chatSwapApplyOneLabel = (): string => t('chat_swap_apply_row')
export function chatSwapApplyOneAria(card: string): string {
  return t('chat_swap_apply_row_aria', { card })
}
export const chatSwapApplyAllLabel = (): string => t('chat_swap_apply_all')

// Confirmation after a successful apply. Used for BOTH the toast and the
// in-transcript applied state, so the two can never drift apart.
export const chatSwapsApplied = (): string => t('chat_swaps_applied')

// Restored from a previous page load. The proposal was checked against the deck
// as it stood then, which we cannot vouch for now, so the cards are shown as
// history with no Apply.
export const chatSwapsHistoryNote = (): string => t('chat_swaps_history_note')

// Fallback when applySwaps failed for a reason that is not a cut conflict. A
// SwapConflictError carries its own sentence, shown through swapConflictMessage().
export const chatSwapApplyFailed = (): string => t('chat_swap_apply_failed')

// Category chips on engine-sourced swaps (`pro:swap_category.<code>`). Unknown
// codes fall back to the raw value, so a new server category shows up readably
// instead of disappearing.
export function swapCategoryLabel(category: string): string {
  return codeLabel('pro', 'swap_category', category) ?? category
}

// ---------------------------------------------------------------------------
// Quota counter (the pill in the panel header)
// ---------------------------------------------------------------------------
//
// There is no server-sent `remaining`, so every string here is built from
// used_month / cap_month. The counter NEVER moves on a local guess: it renders
// whatever /pro/status (or the done-frame push into that cache) last said.

// Free users always see the counter, because the ceiling is the product.
export function freeCounterCopy(used: number, cap: number): string {
  return t('free_counter', { used, cap })
}

// At the cap the pill becomes the upsell itself, so it names the action.
export const freeCounterAtCap = (): string => t('free_counter_at_cap', { go_pro: goProLabel() })

// Pro users only see the counter once they are close enough to the cap for it
// to be information rather than noise. 0.9 of cap_month, computed against the
// SERVER's cap so a plan change needs no client edit.
export const PRO_COUNTER_VISIBLE_RATIO = 0.9

export function proCounterCopy(used: number, cap: number): string {
  return t('pro_counter', { used, cap })
}

// Pro at the cap: no upsell exists above Pro, so the only useful fact is when
// the allowance comes back. Falls back to a plain statement when the server
// omits (or malforms) the reset date rather than printing a raw wire value.
export function proCounterAtCapCopy(resetsAt?: string | null): string {
  const when = formatResetDate(resetsAt)
  return when ? t('pro_counter_at_cap_resets', { when }) : t('pro_counter_at_cap')
}

export const freeCounterAria = (): string => t('free_counter_aria', { product: productName() })

// ---------------------------------------------------------------------------
// Paywall sheet (ProUpgradeModal)
// ---------------------------------------------------------------------------

export const paywallTitle = (): string => productName()
// `finish`: Finish my deck is available to this reader (VITE_FINISH_DECK on
// and, where a /pro/status is in hand, its `finish` block present; see
// ProUpgradeModal). Off, the sheet never names it: the subtitle is the copy
// it had before Finish existed and the benefit is left out.
export const paywallSubtitle = (finish: boolean = false): string =>
  finish ? t('paywall_subtitle_finish', { chat: chatName() }) : t('paywall_subtitle', { chat: chatName() })

// `proMonthlyMessages` is the server's `pro_cap_month` when the caller has a
// status in hand; the fallback constant otherwise.
export function paywallBenefits(proMonthlyMessages: number = PRO_MONTHLY_MESSAGES, finish: boolean = false): readonly string[] {
  return [
    ...(finish ? [t('paywall_benefits.finish_deck')] : []),
    // "in the app", not "on the web": the static blog and guide pages show
    // Mediavine ads to everyone, Pro included, so the promise is scoped to what
    // Pro actually removes. Matches the privacy policy word for word.
    t('paywall_benefits.no_ads'),
    t('paywall_benefits.chat_on_every_deck', { chat: chatName() }),
    t('paywall_benefits.messages_a_month', { messages: proMonthlyMessages }),
    t('paywall_benefits.one_tap_swaps'),
    t('paywall_benefits.set_check'),
    t('paywall_benefits.future_features'),
  ]
}

export const planMonthlyLabel = (): string => t('plan_monthly_label')
export const planAnnualLabel = (): string => t('plan_annual_label')
export const planAnnualNote = (): string => t('plan_annual_note')

// Shown on both plan buttons until RevenueCat is configured. Deliberately not
// "Coming soon": the plans exist, the checkout does not yet.
export const paywallUnavailableLabel = (): string => t('paywall_unavailable_label')
export const paywallUnavailableNote = (): string => t('paywall_unavailable_note')

// Store-purchase latency is real and the receipt arrives before the entitlement
// does, so say so BEFORE the user starts wondering whether it worked.
export const paywallActivationNote = (): string => t('paywall_activation_note')

// ---------------------------------------------------------------------------
// Purchase flow (ProPurchasePanel, the RevenueCat-backed slot)
// ---------------------------------------------------------------------------

// While the offering is being fetched. Prices only appear once RevenueCat has
// answered, so this is what the sheet shows first.
export const paywallLoadingPlans = (): string => t('paywall_loading_plans')

// The offering call failed, or the current offering has neither plan. Nothing
// is purchasable, so no plan buttons are shown at all.
export const paywallPlansUnavailable = (): string => t('paywall_plans_unavailable')

// The action label on a live plan button (replaces paywallUnavailableLabel).
export const paywallSubscribeLabel = (): string => t('paywall_subscribe_label')

// Checkout is open (RevenueCat renders its own form over the page).
export const paywallCheckoutLabel = (): string => t('paywall_checkout_label')

// Charge succeeded, entitlement has not landed yet. The server's entitlement
// cache TTL is 60s and the RevenueCat webhook can lag, so we poll behind this.
export const paywallActivating = (): string => t('paywall_activating')

// Poll succeeded: the entitlement is live.
export const paywallActivatedToast = (): string =>
  t('paywall_activated_toast', { product: productName(), chat: chatName() })

// Poll timed out. This is NOT an error state and must never be styled as one:
// the charge completed, only the entitlement is late.
export const paywallActivationPending = (): string => t('paywall_activation_pending')

// Checkout threw. Deliberately does not claim "you were not charged": the
// client cannot know that, and a wrong reassurance is worse than a hedge.
export const paywallPurchaseFailed = (): string => t('paywall_purchase_failed')

// Manage row, shown to a customer who already has Pro.
export const paywallManageLabel = (): string => t('paywall_manage_label')

// Same correction as accountSubManageFallback, and this one was worse: it
// ignored the failed-lookup case entirely, so an ad blocker on the billing SDK
// sent a web subscriber to Apple, and it named an iOS app that has no paywall
// in it at all.
export const paywallManageFallback = (): string => t('paywall_manage_fallback', { email: SUPPORT_EMAIL })
export const paywallManageGrant = (): string => t('paywall_manage_grant')
export const paywallManageUnavailable = (): string => t('paywall_manage_unavailable', { email: SUPPORT_EMAIL })
// A confirmed store with no link and no directions we can give. Same sentence
// as the account card's accountSubManageNoLink, kept as its own catalog key
// (paywall_manage_no_link) so the two surfaces can be reworded separately.
//
// Says the two true things: the subscription exists, and we could not produce
// its link. The distinction from the manage fallback is the point - that line
// opens with "we do not see a store subscription", which is false the moment
// the server names a store, and it would have fired for `rc_billing`, the
// store this site's own web purchases use.
export const paywallManageNoLink = (): string => t('paywall_manage_no_link', { email: SUPPORT_EMAIL })

// ---------------------------------------------------------------------------
// Google Play (AndroidPlayPurchasePanel and /pro/activated). Inside a
// billing-capable build of the Android app the sheet hands off to the native
// Play paywall, which shows Play's own localized prices. So nothing here
// names a price: a USD fallback would be wrong for most Play customers.
// ---------------------------------------------------------------------------

// The legal line, Play edition: cancelling happens in Google Play, not in our
// account settings. Same <0>/<1> link tags as paywall_legal.
export const paywallLegalPlay = (): string => t('paywall_legal_play')
export const paywallPlayLaunch = (): string => t('paywall_play_launch')
// No link token yet (or the last one was just used): no launch on screen.
export const paywallPlayPreparing = (): string => t('paywall_play_preparing')
export const paywallPlayPlanGroup = (): string => t('paywall_play_plan_group')
// A guest (anonymous) session, or an account without an email: the server
// refuses to bind a purchase to it, so the sheet asks for a sign-in instead.
export const paywallPlaySignin = (): string => t('paywall_play_signin')
export const paywallPlayFailed = (): string => t('paywall_play_failed')
export const paywallPlayRetry = (): string => t('paywall_play_retry')
export const paywallPlayAlreadyPro = (): string => t('paywall_play_already_pro')

// /pro/activated, where the native paywall returns after a purchase.
export const activatedTitle = (): string => t('activated_title')
export const activatedChecking = (): string => t('activated_checking')
export const activatedDone = (): string => t('activated_done', { product: productName() })
export const activatedContinue = (): string => t('activated_continue')
// Not an error: the purchase completed, the entitlement is late.
export const activatedPending = (): string => t('activated_pending')
export const activatedCheckAgain = (): string => t('activated_check_again')
// Signed out is its own state, distinct from "still activating": nothing can
// be checked without the account the purchase was made for.
export const activatedSignedOut = (): string => t('activated_signed_out')
export const activatedSigninCta = (): string => t('activated_signin_cta')

// ---------------------------------------------------------------------------
// Account page: Subscription card
//
// The Terms of Service tell a web subscriber to "cancel from your account
// settings", so this card is the surface that promise points at. Cancelling
// itself happens in RevenueCat's portal (or Apple's), which is why the control
// is a link out rather than a button that mutates entitlement here.
// ---------------------------------------------------------------------------

export const accountSubFree = (): string => t('account_sub_free')
// Names what Pro costs and what it gives, on the page people open when they
// want to buy something. The old line said "you can subscribe from Deck Chat"
// and stopped there, which made the account page a dead end and left the
// paywall reachable only by running out of free messages.
export const accountSubFreeHint = (): string =>
  t('account_sub_free_hint', {
    chat: chatName(),
    messages: PRO_MONTHLY_MESSAGES,
    monthly: priceMonthlyFallback(),
    annual: priceAnnualFallback(),
  })

// The trial, stated where somebody is deciding rather than only inside the
// checkout sheet. Deliberately "new subscribers": the Terms scope it that way,
// and an unqualified promise on this line would be false for anybody
// resubscribing. The precise day count still comes from RevenueCat's own trial
// phase inside the sheet (paywallTrialHook), which is the number checkout
// will actually honour.
export const accountSubTrialHook = (): string => t('account_sub_trial_hook')
export const accountSubUpgradeLabel = (): string => t('account_sub_upgrade_label')

export const accountSubActive = (): string => t('account_sub_active')
export const accountSubGrace = (): string => t('account_sub_grace')
export const accountSubExpired = (): string => t('account_sub_expired')

// The five period lines below are WHOLE sentences with a {{date}} placeholder
// (the date already formatted, formatDateBrowser), never a fragment the card
// splices a date onto: word order and the closing punctuation belong to the
// language ("Renews on {{date}}." / "次回の更新日は{{date}}です。"). The iOS
// SubscriptionCard uses the same key names.
//
// Deliberately "ends", never "renews on". /pro/status carries no will_renew
// flag, and expires_at means the same thing whether or not the customer has
// already cancelled - so "renews on" would state a renewal we cannot see and
// would be false for exactly the person who just used this card to cancel.
export const accountSubPeriodEnd = (date: string): string => t('account_sub_period_end', { date })

// Used only when RevenueCat actually told us. An active subscriber who has NOT
// cancelled is the common case and is entitled to know a charge is coming;
// "current period ends" alone reads the same to them as it does to somebody
// who already cancelled.
export const accountSubRenews = (date: string): string => t('account_sub_renews', { date })
// NOT "Cancelled". willRenew false also arises when the store exhausts its
// billing retries and turns auto-renew off by itself, so naming a customer
// action would misattribute one they never took. RevenueCat exposes
// unsubscribeDetectedAt for that distinction and we do not read it, so we
// state the consequence and claim no cause.
export const accountSubAccessEnds = (date: string): string => t('account_sub_access_ends', { date })

// Grace means the last payment FAILED. The server sets expires_at to
// max(expiration, grace_expiration) here, so the date is the deadline access
// is LOST, never a renewal date - and the headline directly above already
// tells the customer to act. Saying "renews on" under that would tell them
// nothing is required of them.
export const accountSubGraceEnds = (date: string): string => t('account_sub_grace_ends', { date })

// A trial's expiry is the TRIAL's end, so calling the first charge a renewal
// would misdescribe it. The Terms call this out separately and so does this.
export const accountSubTrialConverts = (date: string): string => t('account_sub_trial_converts', { date })
export const accountSubTrialConvertsSuffix = (): string => t('account_sub_trial_converts_suffix')
export const accountSubTrialCancelledSuffix = (): string => t('account_sub_trial_cancelled_suffix')

export const accountSubManageLabel = (): string => t('account_sub_manage_label')

// RevenueCat ANSWERED and has no subscription to manage for this account.
//
// This line used to read "This subscription is billed by the App Store. Cancel
// it in your Apple subscription settings." That was wrong in every case it
// could fire. RevenueCat's SDK documents managementURL as pointing AT the App
// Store for an App Store subscription, so a null is never evidence of one; it
// is what a comped grant, a lapsed subscription, or a purchase RevenueCat
// cannot see looks like. It shipped on the exact surface the Terms of Service
// promise cancellation on, and the first person to read it was a customer who
// had bought on the web and signed in with Google.
export const accountSubManageFallback = (): string => t('account_sub_manage_fallback', { email: SUPPORT_EMAIL })

// The server RESOLVED the store as a comped grant. Now we can say so plainly
// instead of hedging.
// A store the SERVER confirmed, for which we could not produce a link.
//
// This is the ONE case where naming a platform is honest: RevenueCat told us
// which store bills this subscription, so we are repeating a fact rather than
// inferring one from a null. The generic "we do not see a store subscription"
// line would be FALSE here - we see it, we know where it lives, we just could
// not mint a link into it - and it would leave a subscriber with nowhere to
// go, which is the thing the Terms promise not to do.
//
// Closed map on purpose, and it carries the store's NAME as well as the place:
// an earlier version said "billed by the store you bought it from" and then
// named Apple in the very next sentence, which is two levels of confidence in
// one line for a fact the server confirmed outright.
//
// `rc_billing` is deliberately ABSENT rather than forgotten. It is our own web
// billing, and "cancel in your account settings" is circular advice on the
// account settings page. It falls to accountSubManageNoLink below, which is true for
// any confirmed store we cannot give directions for.
//
// The closed map is the `pro:store_cancel` key family, one whole sentence per
// store so a translation never has to splice a store name into a phrase.
export function storeCancelCopy(store: string | null): string | null {
  if (!store) return null
  return codeLabel('pro', 'store_cancel', store)
}

// A confirmed store with no link and no directions we can give. Says the two
// true things: the subscription exists, and we could not produce its link.
//
// The distinction from the manage fallback is the whole point. That line opens
// with "we do not see a store subscription", which is false the moment the
// server names a store - and it would have fired for `rc_billing`, the store
// this site's own web purchases use, for the audience the Terms address
// FIRST.
// Stores RevenueCat reports for access we handed out ourselves: nothing is
// being charged and there is nothing to cancel.
//
// Shared rather than duplicated. The account card held this as a Set and the
// paywall's manage row hard-coded the string, and those two surfaces carry
// comments in both files insisting they must never diverge - which is exactly
// the shape that lets a future addition land in one and miss the other.
export const GRANT_STORES: ReadonlySet<string> = new Set(['promotional'])

export const accountSubManageNoLink = (): string => t('account_sub_manage_no_link', { email: SUPPORT_EMAIL })

export const accountSubManageGrant = (): string => t('account_sub_manage_grant')

// Shown when the lookup FAILED. Deliberately names no platform: a blocked
// billing SDK is exactly how a web subscriber reaches this line, and telling
// them to cancel at Apple would be both false and unactionable.
export const accountSubManageUnavailable = (): string => t('account_sub_manage_unavailable', { email: SUPPORT_EMAIL })

export const paywallCloseLabel = (): string => t('paywall_close_label')
export const paywallAriaLabel = (): string => t('paywall_aria_label', { product: productName() })

export const paywallTermsLabel = (): string => t('paywall_terms_label')
export const paywallPrivacyLabel = (): string => t('paywall_privacy_label')

// TODO(pro): point TERMS_URL at a real terms page. iOS ships the standard Apple
// EULA (https://www.apple.com/legal/internet-services/itunes/dev/stdeula/),
// which is an App Store convention and NOT the right document for a web
// purchase, so the web build points at /privacy until terms are written.
export const TERMS_URL = '/privacy'
export const PRIVACY_URL = '/privacy'

// ---------------------------------------------------------------------------
// Error copy (consumed by chatAdapter.mapChatError - keep the two in sync)
// ---------------------------------------------------------------------------

// Free user at the monthly cap. This is the ONLY copy that opens the paywall.
export const errorQuotaFree = (): string => t('error_quota_free', { go_pro: goProLabel() })
// The same cap inside the Android app, which sells nothing: no "Go Pro".
export const errorQuotaFreeNoUpgrade = (): string => t('error_quota_free_app')

// Pro user at a cap. Functions because the numbers and the reset date come off
// the wire; the fallbacks avoid printing "undefined" when the server omits them.
// One whole sentence per case, so a translation never has to splice phrases.
export function proMonthlyCapCopy(cap: number, resetsAt?: string): string {
  const when = formatResetDate(resetsAt)
  if (cap > 0) return when ? t('pro_monthly_cap.count_resets', { cap, when }) : t('pro_monthly_cap.count', { cap })
  return when ? t('pro_monthly_cap.all_resets', { when }) : t('pro_monthly_cap.all')
}

export function proDailyCapCopy(cap: number, resetsAt?: string): string {
  const when = formatResetDate(resetsAt)
  if (cap > 0) return when ? t('pro_daily_cap.count_resets', { cap, when }) : t('pro_daily_cap.count', { cap })
  return when ? t('pro_daily_cap.all_resets', { when }) : t('pro_daily_cap.all')
}

export const errorSpendCap = (): string => t('error_spend_cap', { chat: chatName() })

export function rateLimitedCopy(retryAfterSeconds?: number): string {
  if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) {
    return t('rate_limited_seconds', { count: retryAfterSeconds })
  }
  return t('rate_limited')
}

export const errorUnauthorized = (): string => t('error_unauthorized')

export const errorServiceUnavailable = (): string => t('error_service_unavailable', { chat: chatName() })

// Server told us it refunded the message, so we may safely say it did not count.
export const errorRefundedTimeout = (): string => t('error_refunded_timeout')
export const errorRefundedUpstream = (): string => t('error_refunded_upstream', { chat: chatName() })

// Ambiguous failures (network break, client-side ceiling, truncated stream).
// NEVER claim "not charged" here: we genuinely do not know whether the server
// reserved the message, and a wrong reassurance is worse than an honest hedge.
export const errorAmbiguous = (): string => t('error_ambiguous')

// Renders a server date (YYYY-MM-DD or an ISO timestamp) as a short local date.
// Returns null for anything unparseable so callers drop the clause entirely
// rather than print a raw wire value at the user.
export function formatResetDate(raw?: string | null): string | null {
  if (!raw) return null
  // A bare YYYY-MM-DD is parsed as UTC midnight by Date.parse, which renders as
  // the PREVIOUS day west of Greenwich. Build it as a local date instead so the
  // reset date the server means is the date the user reads.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  const when = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(Date.parse(raw))
  if (Number.isNaN(when.getTime())) return null
  // eslint-disable-next-line i18next/no-literal-string -- Intl options, not text
  return formatDateBrowser(when, { month: 'short', day: 'numeric' })
}
